import React from "react";
import { useNavigate } from "react-router-dom";
import css from "./failedLessonScreen.module.css";
import { useTranslation } from "react-i18next";

const SECTIONS = [
    {
        key: "video",
        labelKey: "Video",
        needKey: "Watch the video to complete this section.",
        actionKey: "Repeat video",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
        ),
    },
    {
        key: "vocabulary",
        labelKey: "Words",
        needKey: "Open the words section to complete it.",
        actionKey: "Repeat words",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
        ),
    },
    {
        key: "quiz",
        labelKey: "Quiz",
        needKey: "Get at least 5 correct quiz answers.",
        actionKey: "Repeat quiz",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <path d="M12 17h.01" />
            </svg>
        ),
    },
    {
        key: "ai_dialog",
        labelKey: "Speaking",
        needKey: "Finish a qualifying AI dialogue.",
        actionKey: "Repeat AI Speaking",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            </svg>
        ),
    },
];

const FailedLessonScreen = ({ lesson, progress, onRepeat, onGoToSection }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const incomplete = SECTIONS.filter((section) => progress?.sections?.[section.key]?.status !== "completed");

    const handleSection = (sectionKey) => {
        if (onGoToSection) {
            onGoToSection(sectionKey);
        } else if (onRepeat) {
            onRepeat();
        }
    };

    return (
        <div className={css.wrap}>
            <div className={css.hero}>
                <div className={css.heroIcon}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 8v4" />
                        <path d="M12 16h.01" />
                    </svg>
                </div>
                <h1>{t("Almost there!")}</h1>
                <p>{t("To pass the lesson, complete all four sections: video, words, quiz and AI dialog.")}</p>
            </div>

            <div className={css.conds}>
                {SECTIONS.map((section) => {
                    const passed = progress?.sections?.[section.key]?.status === "completed";
                    return (
                        <div key={section.key} className={`${css.cond} ${passed ? css.condPass : css.condFail}`}>
                            <div className={css.condTop}>
                                <div className={css.condLabel}>
                                    {section.icon}
                                    {t(section.labelKey)}
                                </div>
                                <div className={css.condBadge}>
                                    {passed ? <>{t("Completed")} ✓</> : t("Not completed")}
                                </div>
                            </div>
                            <div className={css.condNeed}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                    <path d="m9 11 3 3L22 4" />
                                </svg>
                                {passed ? t("This section is complete.") : t(section.needKey)}
                            </div>
                            <div className={css.condBar}>
                                <div className={css.condBarFill} style={{ width: passed ? "100%" : "0%" }}></div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className={css.actions}>
                {incomplete.length > 0 && (
                    <div className={css.actRow}>
                        {incomplete.map((section) => (
                            <button
                                key={section.key}
                                className={`${css.actBtn} ${section.key === "ai_dialog" ? css.actSpeak : css.actQuiz}`}
                                onClick={() => handleSection(section.key)}
                            >
                                {section.icon}
                                {t(section.actionKey)}
                            </button>
                        ))}
                    </div>
                )}
                <button className={`${css.actBtn} ${css.actBack}`} onClick={() => navigate("/roadMap")}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5" />
                        <path d="m12 19-7-7 7-7" />
                    </svg>
                    {t("Return to course")}
                </button>
            </div>

            <div className={css.encourage}>
                <div className={css.encIcon}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3L12 3Z" />
                    </svg>
                </div>
                <div className={css.encText}>
                    <strong>{t("That is okay!")}</strong>{" "}
                    {t("Finish the remaining sections, and the lesson will be passed.")}
                </div>
            </div>
        </div>
    );
};

export default FailedLessonScreen;
